import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { Router } from '@angular/router';
import { Utils } from 'src/app/core/resources/utils';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { AuthService } from 'src/app/core/services/authentication.service';
import { DateManager } from 'src/app/core/managers/date.manager';
import { CargoStateEnum } from 'src/app/core/enums/cargoState.enum';
import { ServiceType } from 'src/app/core/interfaces/serviceType';
import { CargoManager } from 'src/app/core/managers/cargo.manager';
import { CargoService } from 'src/app/core/services/cargo.service';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { AddressCargo } from 'src/app/core/interfaces/addressCargo';
import { Destination } from 'src/app/core/interfaces/destination';


@Component({
  selector: 'app-cargo-renew-dates',
  templateUrl: './cargo-renew-dates.component.html',
  styleUrls: ['./cargo-renew-dates.component.scss']
})
export class CargoRenewDatesComponent implements OnInit {

  public dateLoad: string;
  public dateDownload: string[] = [];
  public destinationList: any[] = [];
  public cargo: Cargo;
  configDateLoad = {
    min: new Date(),
    max: DateManager.add(new Date(), 30, 'days')
  };
  minDateDownload = null;
  numberOfRenew = 1;
  transportCompanyControl = new FormControl(null);

  constructor(
    private cargoService: CargoService,
    private authService: AuthService,
    private spinner: NgxSpinnerService,
    public dialogRef: MatDialogRef<CargoRenewDatesComponent>,
    public dialog: MatDialog,
    public utils: Utils,
    public cargoManager: CargoManager,
    private snackBarService: SnackBarService,
    @Inject(MAT_DIALOG_DATA) public data: { cargo: Cargo, isDuplicate?: boolean },
    private router: Router,
  ) { }

  ngOnInit() {
    this.cargo = this.data.cargo;
    this.destinationList = this.cargo.cargoFeature.uploadDownload.destination;
    this.transportCompanyControl.setValue(this.cargo.transportCompany || null);
  }

  get isTransportService(): boolean {
    return this.utils.isTransportService(this.cargo);
  }

  /** El servicio duplicado solo declara transportadora si el original la tenía. */
  get requiresTransportCompany(): boolean {
    return !!(this.authService && this.authService.hasTransportServicesCompany) && this.isTransportService;
  }

  onChangeNumberOfRenew(event: Event) {
    const input = event.target as HTMLInputElement;
    this.numberOfRenew = parseInt(input.value) || 1;
  }

  async renewDates() {
    const hasAllDownloadDates = this.destinationList.every((destination, index) =>
      this.utils.isDefined(this.dateDownload[index])
    );
    if (!this.dateLoad || !hasAllDownloadDates)
      return this.snackBarService.openSnackBar('Hay fechas sin llenar', undefined, 'alert');
    if (this.numberOfRenew < 1 || this.numberOfRenew > 20)
      return this.snackBarService.openSnackBar('El número de duplicados debe ser entre 1 y 20', undefined, 'alert');
    let newCargo: Cargo = this.utils.clone(this.cargo);
    newCargo.dateLoad = this.dateLoad;
    newCargo.cargoFeature.uploadDownload.origin.addresses.forEach(address => delete address.idTracking);
    newCargo.cargoFeature.uploadDownload.destination.forEach((destination, index) => {
      destination.downloadDate = this.dateDownload[index]
      destination.addresses.forEach(address => delete address.idTracking);
    });
    const cargoToRenew = await this.clearDataCargoToRenew(newCargo);
    this.spinner.show();
    let cargoList: Cargo[] = [];
    try {
      const cargos = await this.cargoService.duplicateCargo([cargoToRenew, ...Array(this.numberOfRenew - 1).fill(cargoToRenew)], this.cargo.id).toPromise();
      if (cargos && cargos.length) cargoList = [...cargos];
    } catch (error) {
      this.spinner.hide();
      return this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'alert');
    }
    this.spinner.hide();
    this.cargoManager.modalCargoCreation(cargoList, `Tu servicio ha sido renovado con los siguientes consecutivos`);
    this.dialogRef.close();
  }

  private clearDataCargoToRenew(cargo: Cargo): Cargo {
    let newCargo: Cargo = {
      dateLoad: cargo.dateLoad,
      numberDocumentCreatorLoad: this.authService.getUserSession().information.document,
      idCompany: cargo.idCompany,
      exclusive: cargo.exclusive,
      shippingCost: cargo.shippingCost,
      creationDate: DateManager.dateToString(new Date()),
      createdBy: {
        userId: this.authService.getUserSession().information.document,
        userName: this.authService.getUserSession().information.name,
        date: DateManager.dateToString(new Date()),
      },
      prevalidation: { valid: false }
    };
    // El duplicado solo lleva transportadora si el original la declaraba.
    if (this.requiresTransportCompany) { newCargo.transportCompany = this.transportCompanyControl.value; }
    delete newCargo.shippingCost.payments;
    delete newCargo.shippingCost.fopatPercentage;
    delete newCargo.shippingCost.fopatValue;

    const renewFields = [
      'observation',
      'numberDocumentSender',
      'distancy',
      'estimatedTime',
      'cargoOwner',
      'additionalCosts',
      'cargoModel',
      'ministry',
      'escortedVehicle',
      'manifestType',
      'riskProfile',
      'zone',
      'axles',
      'monitoringCompany'
    ]

    renewFields.forEach(field => {
      if (this.utils.isDefined(this.utils.getNestedValue(cargo, field))) {
        newCargo[field] = cargo[field];
      }
    });
    const letterRetirement = this.utils.getNestedValue(cargo, 'cargoModel.tripType.letterRetirement');
    if (letterRetirement) delete newCargo.cargoModel.tripType.letterRetirement;

    const travelExpenseGlobalStatus = this.utils.getNestedValue(cargo, 'cargoModel.travelExpenseGlobalStatus');
    if (travelExpenseGlobalStatus) delete newCargo.cargoModel.travelExpenseGlobalStatus;

    if (this.utils.isSatelliteLockService(cargo) && cargo.lock && cargo.lock.type && cargo.lock.type.id && cargo.lock.type.name)
      newCargo['lock'] = { type: cargo.lock.type };

    if (!this.utils.isEmpty(cargo.cargoFeature)) {
      newCargo.cargoFeature = cargo.cargoFeature;
      if (!this.utils.isEmpty(cargo.cargoFeature.uploadDownload)) {
        newCargo.cargoFeature.uploadDownload.origin.addresses = cargo.cargoFeature.uploadDownload.origin.addresses.map((address) => {
          const addressToKeep: AddressCargo = {
            address: this.utils.replaceAmpersand(address.address),
            cellphone: address.cellphone,
            id: address.id,
            location: address.location,
            time: address.time,
            timePact: address.timePact,
            minutePact: address.minutePact,
            quantity: address.quantity,
            thirdPartyConsignment: address.thirdPartyConsignment,
          };
          return addressToKeep;
        });
        newCargo.cargoFeature.uploadDownload.destination = cargo.cargoFeature.uploadDownload.destination.map((destination) => {
          const destinationToKeep: Destination = { ...destination, addresses: [] };
          destinationToKeep.addresses = destination.addresses.map((address) => {
            const addressToKeep: AddressCargo = {
              address: this.utils.replaceAmpersand(address.address),
              cellphone: address.cellphone,
              id: address.id,
              location: address.location,
              time: address.time,
              timePact: address.timePact,
              minutePact: address.minutePact,
              quantity: address.quantity,
              thirdPartyConsignment: address.thirdPartyConsignment,
            };
            return addressToKeep;
          });
          return destinationToKeep;
        });
      }
    }
    newCargo.state = CargoStateEnum.REQUEST;
    return newCargo;
  }

  onChangeDatePicker($event: any, type: string, index?: number) {
    if (type === "load") {
      this.dateLoad = DateManager.dateToString($event.value, 'YYYY-MM-DD HH:mm ZZ');
    } else {
      this.dateDownload[index] = DateManager.dateToString($event.value, 'YYYY-MM-DD HH:mm ZZ');
    }
  }

  focusDownloadInput(indexDownload: number) {
    if (indexDownload === 0) {
      this.minDateDownload = new Date(this.dateLoad);
    } else {
      this.minDateDownload = this.getMinDateDownload(indexDownload);
    }
  }

  getMinDateDownload(index: number) {
    const lastDate = this.dateDownload[index - 1];
    if (lastDate) {
      return new Date(lastDate);
    } else {
      return {};
    }
  }

}
